import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {getMonthNamesInstance} from '../../utils/MonthNames';
import {getDayNamesInstance} from '../../utils/DayNames';
import {getCalendarDataInstance} from '../../utils/CalendarData';
import WeekdayNames from '../WeekdayNames/WeekdayNames';
import Day from '../Day/Day';
import dateUtils from '../../utils/dateUtils';
import todayUtil from '../../utils/today';
import propTypeDateString from '../../utils/propTypeDateString';
import classNames from 'classnames';
import './Month.less';

class Month extends Component {
    static propTypes = {
        year: PropTypes.number,
        month: PropTypes.number,
        width: PropTypes.string,
        simple: PropTypes.bool,
        showMonthLabels: PropTypes.bool,
        showGridlines: PropTypes.bool,
        monthLabelAlignment: PropTypes.string,
        padWeeks: PropTypes.bool,
        scrollToMonth: PropTypes.bool,
        hoverDate: PropTypes.string,
        showSiblingMonths: PropTypes.bool,
        selectable: PropTypes.bool,
        selectionType: PropTypes.oneOf(['range', 'single']),
        selectionStart: propTypeDateString,
        selectionEnd: propTypeDateString,
        selectedDate: propTypeDateString,
        onMonthNameClicked: PropTypes.func,
        onDayClick: PropTypes.func,
        onDayHover: PropTypes.func,
        dayData: PropTypes.object, // eslint-disable-line react/forbid-prop-types
        dayTemplateComponent: PropTypes.func,
        /** A moniker or BCP-47 style language tag string. */
        locale: PropTypes.string,
        languageTag: PropTypes.object,
        /** Flag to specify whether year should be displayed first*/
        displayYearFirst: PropTypes.bool,
        /** Optional labels object to override default locale-driven labels */
        labels: PropTypes.shape({
            /** To support year labels for locales that need them e.g. ja-JP*/
            yearLabel: PropTypes.string,
            monthNames: PropTypes.arrayOf(PropTypes.string),
            shortMonthNames: PropTypes.arrayOf(PropTypes.string),
            dayNames: PropTypes.arrayOf(PropTypes.string),
            shortDayNames: PropTypes.arrayOf(PropTypes.string)
        }),
        index: PropTypes.number
    };

    static defaultProps = {
        dayData: {},
        padWeeks: false,
        scrollToMonth: false,
        simple: false,
        displayYearFirst: false,
        labels: {
            yearLabel: ''
        }
    };

    constructor(props) {
        super(props);

        // Initialize locale based calendar data.
        this.dayNames = getDayNamesInstance(props.locale, props.labels);
        this.monthNames = getMonthNamesInstance(props.locale, props.labels);
        this.calendarData = getCalendarDataInstance(props.showSiblingMonths);
        this.calendarData.setProperty('weekStart', this.dayNames.getStartOfWeek());
        this.reference = [];

        this.state = {
            calendarDays: this.getCalendarDays(props)
        };
    }

    /* istanbul ignore next */
    componentDidMount() {
        const date = new Date();
        const isCurrentMonth = date.getMonth() === this.props.month && date.getFullYear() === this.props.year;
        // If this is the current month or the month contains the selected dates then scroll into view
        if (this.props.scrollToMonth && ((!this.props.selectionStart && isCurrentMonth) || this.isSelectedMonth())) {
            this.elem.scrollIntoView();
        }
    }

    // eslint-disable-next-line complexity
    componentWillReceiveProps(nextProps) {
        const {year, month, locale, showSiblingMonths, labels} = nextProps;
        const localeChange = locale !== this.props.locale;
        const labelsChange = labels !== this.props.labels;
        const siblingChange = showSiblingMonths !== this.props.showSiblingMonths;

        if (localeChange || labelsChange) {
            this.dayNames = getDayNamesInstance(locale, labels);
            this.monthNames = getMonthNamesInstance(locale, labels);
        }

        if (siblingChange) {
            this.calendarData = getCalendarDataInstance(showSiblingMonths);
        }

        if (localeChange || siblingChange) {
            this.calendarData.setProperty('weekStart', this.dayNames.getStartOfWeek());
        }

        // If these guys change, get the new calendar day data
        if (year !== this.props.year ||
            month !== this.props.month ||
            localeChange || siblingChange) {
            this.setState({
                calendarDays: this.getCalendarDays(nextProps)
            });
        }
    }

    // The optimizations in shouldComponentUpdate are for improving the responsiveness
    // when making selections, particularly when many months are rendered.
    shouldComponentUpdate(nextProps) {
        const propChange = ['month', 'year', 'width', 'simple', 'showMonthLabels',
            'monthLabelAlignment', 'showGridLines', 'padWeeks',
            'showSiblingMonths', 'selectable', 'dayData', 'selectionType',
            'locale', 'labels', 'index'].some(
            (prop) => {
                return nextProps[prop] !== this.props[prop];
            });

        // Always update if any of the above properties change
        if (propChange) {
            return true;
        }

        if (nextProps.selectionType === 'single') {
            return this.shouldComponentUpdateSingle(nextProps);
        }
        return this.shouldComponentUpdateRange(nextProps);
    }

    zeroFill(value) {
        const oneBasedValue = value + 1;
        return oneBasedValue < 10 ? `0${oneBasedValue}` : `${oneBasedValue}`;
    }

    getRenderingMonth(year, month) {
        const zeroFilledMonth = this.zeroFill(month);
        return +`${year}${zeroFilledMonth}`;
    }

    shouldComponentUpdateSingle(nextProps) {
        const {month, year, selectedDate} = this.props;

        const renderingMonth = this.getRenderingMonth(year, month);
        const selectedMonth = this.getMonth(nextProps.selectedDate);
        const prevSelectedMonth = this.getMonth(selectedDate);
        const buffer = nextProps.showSiblingMonths ? 1 : 0;

        // Clear any selections (i.e. render any month with a previous selection)
        if (renderingMonth === prevSelectedMonth) {
            return true;
        }

        // Skip any months outside of the new selection, plus a buffer (if applicable)
        // Using an extra buffer because the early dates from the next month may show in
        // the previous month. Same for next month dates (showSiblingMonths prop)
        if (renderingMonth < selectedMonth - buffer ||
            renderingMonth > selectedMonth + buffer) {
            return false;
        }

        return true;
    }

    shouldComponentUpdateRange(nextProps) {
        const {month, year, selectionStart, selectionEnd, hoverDate} = this.props;

        const renderingMonth = this.getRenderingMonth(year, month);
        const selectionStartMonth = this.getMonth(nextProps.selectionStart);
        const selectionEndMonth = this.getMonth(nextProps.selectionEnd);
        const hoverMonth = this.getMonth(nextProps.hoverDate);
        const outMonth = selectionEndMonth || hoverMonth || selectionStartMonth;
        const buffer = nextProps.showSiblingMonths ? 1 : 0;

        // If the new selection, or partial selection contains the month in question, render.
        // Using an extra buffer because the early dates from the next month may show in
        // the previous month. Same for next month dates (showSiblingMonths prop)
        if (renderingMonth >= selectionStartMonth - buffer && renderingMonth <= outMonth + buffer) {
            return true;
        }

        const prevSelectionStartMonth = this.getMonth(selectionStart);
        const prevSelectionEndMonth = this.getMonth(selectionEnd);
        const prevHoverMonth = this.getMonth(hoverDate);
        const outMonth2 = prevSelectionEndMonth || Math.max(prevHoverMonth, prevSelectionStartMonth);

        // If an old selection was drawn on this month, rerender to clear
        if (renderingMonth >= prevSelectionStartMonth && renderingMonth <= outMonth2) {
            return true;
        }

        return false;
    }

    getMonth(dateString) {
        // concatenating the year and month 201611, for comparisons
        if (dateString) {
            const parts = dateString.split('-');
            return +`${parts[0]}${parts[1]}`;
        }
        return null;
    }

    // Determines if this month contains the start date of the current selection
    isSelectedMonth() {
        let dateString = this.props.selectedDate || this.props.selectionStart;

        if (!dateString) {
            return false;
        }

        let date = dateUtils.getDateFromString(dateString);

        return date.getMonth() === this.props.month &&
                date.getFullYear() === this.props.year;
    }

    newFilledArray(len, val) {
        let a = [];
        while (len--) {
            a.push(val);
        }
        return a;
    }

    getCalendarDays({month, year, width}) {
        let days = this.calendarData.getMonth(year, month);
        days = days.map((day) => {
            if (!day) {
                return day;
            }

            day.dateString = dateUtils.getDayToString(day);
            return day;
        });

        // Pad the days array so that all months have a height of
        // 6 weeks, this is only when multiple months are showing.
        if (width !== '100%' || this.props.padWeeks) {
            const paddingArray = this.newFilledArray(42 - days.length, false);
            days = days.concat(paddingArray);
        }

        return days;
    }

    handleMonthNameClicked = () => {
        if (this.props.onMonthNameClicked) {
            this.props.onMonthNameClicked(this.props.month, this.props.year);
        }
    }

    getRow() {
        let focusedElement = document.activeElement; // Find the element that currently has focus
        return parseInt(focusedElement.dataset.row, 10);
    }

    getCol() {
        let focusedElement = document.activeElement; // Find the element that currently has focus
        return parseInt(focusedElement.dataset.column, 10);
    }

    handleDayKeyboardEvents = (e) => {
        const row = this.getRow();
        const col = this.getCol();
        const numOfRows = this.reference.length - 1;
        const keyCode = e.which;

        if (keyCode === 39) { // Right Arrow
            this.props.handleRightArrow(row, col, numOfRows);
        } else if (keyCode === 37) { // Left Arrow
            this.props.handleLeftArrow(row, col);
        } else if (keyCode === 40) { // Down Arrow
            this.props.handleDownArrow(row, col, numOfRows);
        } else if (keyCode === 38) { // Up Arrow
            this.props.handleUpArrow(row, col);
        }
    }

    // Used to fill a 2 dimension array to keep a reference for each day: reference[week][day]
    pushDayRef = (day, rowIndex) => {
        if (day) {
            this.reference[rowIndex].push(day);
        }
    }

    setActiveMonth = () => {
        this.props.setActiveMonth(this.props.index);
    }

    /**
    * Determines whether a day should be focusable. If there is not a day selected then a focusable day is either
    * the current day of the present month or the first day of the month as long as it is not in the past
    * @param day {object} The custom day object to check
    * @param i {number} index of month
    * @returns {boolean} true if day is the focusable day of the given month, false otherwise
    */
    firstFocusableDay = (day, i, tableRow) => { // eslint-disable-line complexity
        const today = todayUtil.getToday();
        const isToday = todayUtil.isToday(day, today);
        const isPast = todayUtil.isPast(day, today);
        const numMonths = this.props.numMonthsToDisplay;
        const dateString = this.props.selectedDate || this.props.selectionStart;
        let startDate = dateString ? dateUtils.getDateFromString(dateString) : null;
        let outOfRange = true;
        let monthDiff;
        let yearDiff;

        if (startDate !== null && startDate !== '') {
            yearDiff = day.year - startDate.getFullYear();
            monthDiff = Math.abs((day.month + (yearDiff * 12)) - startDate.getMonth());
            outOfRange = monthDiff >= numMonths;

            if (startDate.getMonth() < day.month && startDate.getFullYear() <= day.year) {
                outOfRange = true;
            }
        }

        if (this.props.selectionStart === null || this.props.selectionStart === '' || outOfRange) {
            if (i === 0 && !isPast) { // focusable day must be within the first month displayed (props.index === 0)
                if (isToday) {
                    return true;
                } else if (dateUtils.isFirstDayInMonth(day) && tableRow === 0) { // Verifying the table row is 0 prevents potential sibling months from also receiving focus on their first day
                    return true;
                }
            }
        }

        return false;
    }

    /**
     * Creates the label for the month to display based on whether the month should be displayed first or not
     */
    renderMonthLabel = (labelClasses, month, year) => {
        const yearName = (this.props.labels.yearLabel) ? this.props.labels.yearLabel : '';
        const monthLabel = (<span> {this.monthNames.getMonthNames()[month]}</span>);
        const yearLabel = (<span className="year-label"> {year.toString() + yearName}</span>);
        const displayLabels = this.props.displayYearFirst ? (<span>{yearLabel}{monthLabel}</span>)
            : (<span>{monthLabel}{yearLabel}</span>);
        return (
            <h4 className={labelClasses} onClick={this.handleMonthNameClicked}>
                {displayLabels}
            </h4>);
    }

    render() {
        const {
            month,
            year,
            simple,
            locale,
            width,
            showGridlines,
            showMonthLabels,
            monthLabelAlignment,
            hoverDate,
            selectable,
            selectionStart,
            selectionEnd,
            selectedDate,
            onMonthNameClicked,
            onDayClick,
            onDayHover,
            dayData,
            dayTemplateComponent,
            onDisabledDayClick,
            i18n,
            index
        } = this.props;

        const calendarDays = this.state.calendarDays;
        const multi = (width !== '100%');
        this.reference = [];

        const monthClasses = classNames({
            'month': true,
            multi,
            simple
        });

        const labelClasses = classNames(`align-${monthLabelAlignment}`, {
            'hoverable': onMonthNameClicked,
            'h3': !multi
        });

        return (
            <div className={monthClasses} style={{width}} ref={(ref) => (this.elem = ref)}>

                {showMonthLabels && this.renderMonthLabel(labelClasses, month, year)}

                {/**
                * class="notranslate" tells Google Translate to avoid this block.
                * Shortened weekday names and day numbers are being translated wrong sometimes.
                */}
                <table className="month-table notranslate" aria-label={`${this.monthNames.getMonthNames()[month]} ${year}`}>
                    <thead>
                        <WeekdayNames
                            simple={simple}
                            startOfWeek={this.dayNames.getStartOfWeek()}
                            dayNames={this.dayNames.getDayNames()}
                            shortDayNames={this.dayNames.getShortDayNames()}
                            longDayNames={this.dayNames.getLongDayNames()}
                            showGridlines={showGridlines}
                        />
                    </thead>
                    <tbody className="day-wrap" onKeyDown={this.handleDayKeyboardEvents}>
                        {/* All days of month */}
                        {calendarDays.reduce((m, day, i) => {
                            if (i % 7 === 0) {
                                m.push([day]);
                            } else {
                                m[m.length - 1].push(day);
                            }
                            return m;
                        }, []).map((grouped, j) => {
                            this.reference.push([]);
                            return (
                                <tr key={j}>
                                    {
                                        grouped.map((day, k) =>
                                            <Day
                                                key={`day-${k}`}
                                                day={day}
                                                weekIndex={j}
                                                dayIndex={k}
                                                monthIndex={index}
                                                locale={locale}
                                                simple={simple}
                                                showGridlines={showGridlines}
                                                selectable={selectable}
                                                selectionStart={selectionStart}
                                                selectionEnd={selectionEnd}
                                                selectedDate={selectedDate}
                                                hoverDate={hoverDate}
                                                onDayClick={onDayClick}
                                                onDayHover={onDayHover}
                                                dayData={dayData[day.dateString] || dayData.defaults || {}}
                                                dayTemplateComponent={dayTemplateComponent}
                                                onDisabledDayClick={onDisabledDayClick}
                                                languageTag={this.props.languageTag}
                                                setActiveMonth={this.setActiveMonth}
                                                isFocusable={this.firstFocusableDay(day, index, j)}
                                                i18n={i18n}
                                                setRef={(c) => (this.pushDayRef(c, j))} // eslint-disable-line
                                            />
                                        )
                                    }
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    }
}

export default Month;
